import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Fuel, MapPin, Filter, Bell, TrendingDown } from 'lucide-react';
import GasStationMap from './components/GasStationMap';
import BestPricesPanel from './components/BestPricesPanel';
import { logAnalyticsEvent, logPageView } from './firebase';
import './App.css';

// Lithuanian cities and municipalities coordinates mapping
const CITY_COORDS = {
  'Vilnius': { lat: 54.6872, lng: 25.2797 },
  'Kaunas': { lat: 54.8985, lng: 23.9036 },
  'Klaipėda': { lat: 55.7033, lng: 21.1443 },
  'Šiauliai': { lat: 55.9349, lng: 23.3136 },
  'Panevėžys': { lat: 55.7348, lng: 24.3570 },
  'Alytus': { lat: 54.3963, lng: 24.0458 },
  'Marijampolė': { lat: 54.5594, lng: 23.3544 },
  'Mažeikiai': { lat: 56.3089, lng: 22.3414 },
  'Jonava': { lat: 55.0722, lng: 24.2797 },
  'Utena': { lat: 55.4975, lng: 25.5997 },
  'Kėdainiai': { lat: 55.2894, lng: 23.9747 },
  'Telšiai': { lat: 55.9814, lng: 22.2472 },
  'Tauragė': { lat: 55.2508, lng: 22.2906 },
  'Ukmergė': { lat: 55.2467, lng: 24.7561 },
  'Visaginas': { lat: 55.5969, lng: 26.4419 },
  'Plungė': { lat: 55.9114, lng: 21.8453 },
  'Kretinga': { lat: 55.8894, lng: 21.2447 },
  'Šilutė': { lat: 55.3494, lng: 21.4664 },
  'Radviliškis': { lat: 55.8108, lng: 23.5458 },
  'Palanga': { lat: 55.9175, lng: 21.0678 },
  'Gargždai': { lat: 55.7094, lng: 21.3958 },
  'Druskininkai': { lat: 54.0189, lng: 23.9722 },
  'Rokiškis': { lat: 55.9667, lng: 25.5833 },
  'Biržai': { lat: 56.2000, lng: 24.7500 },
  'Elektrėnai': { lat: 54.7833, lng: 24.6667 },
  'Garliava': { lat: 54.8261, lng: 23.8750 },
  'Kuršėnai': { lat: 56.0000, lng: 22.9333 },
  'Jurbarkas': { lat: 55.0778, lng: 22.7667 },
  'Vilkaviškis': { lat: 54.6514, lng: 23.0353 },
  'Raseiniai': { lat: 55.3833, lng: 23.1167 },
  'Anykščiai': { lat: 55.5264, lng: 25.1031 },
  'Lentvaris': { lat: 54.6436, lng: 25.0500 },
  'Grigiškės': { lat: 54.6761, lng: 25.0861 },
  'Prienai': { lat: 54.6333, lng: 23.9500 },
  'Joniškis': { lat: 56.2333, lng: 23.6167 },
  'Kelmė': { lat: 55.6333, lng: 22.9333 },
  'Varėna': { lat: 54.2167, lng: 24.5833 },
  'Kaišiadorys': { lat: 54.8667, lng: 24.4500 },
  'Pasvalys': { lat: 56.0597, lng: 24.3975 },
  'Kupiškis': { lat: 55.8333, lng: 24.9667 },
  'Zarasai': { lat: 55.7333, lng: 26.2500 },
  'Skuodas': { lat: 56.2667, lng: 21.5167 },
  'Švenčionys': { lat: 55.1333, lng: 26.1500 },
  'Ignalina': { lat: 55.3500, lng: 26.1667 },
  'Širvintos': { lat: 55.0500, lng: 24.9500 },
  'Molėtai': { lat: 55.2333, lng: 25.4167 },
  'Naujoji Akmenė': { lat: 56.3167, lng: 22.9000 },
  'Šakiai': { lat: 54.9500, lng: 23.0500 },
  'Šalčininkai': { lat: 54.3000, lng: 25.3833 },
  'Kalvarija': { lat: 54.4167, lng: 23.2333 },
  'Trakai': { lat: 54.6378, lng: 24.9347 },
  'Akmenė': { lat: 56.2450, lng: 22.7472 },
  'Pakruojis': { lat: 56.0667, lng: 23.9333 },
  'Lazdijai': { lat: 54.2333, lng: 23.5167 },
  'Neringa': { lat: 55.4500, lng: 21.0667 },
  'Pagėgiai': { lat: 55.1333, lng: 21.9167 },
  'Kazlų Rūda': { lat: 54.7500, lng: 23.4833 },
  'Rietavas': { lat: 55.7167, lng: 21.9333 },
  'Šilalė': { lat: 55.4833, lng: 22.1833 },
  'Venta': { lat: 56.1833, lng: 21.9167 },
  'Eišiškės': { lat: 54.1667, lng: 24.7000 },
  'Pabradė': { lat: 54.9833, lng: 25.7667 },
  'Salantai': { lat: 56.0667, lng: 21.5667 }
};

const getInitialPanelTheme = () => {
  return getSystemPanelTheme();
};

const normalizeBrand = (brand = '') => {
  return brand
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
};

const LOGO_BASE_PATH = `${process.env.PUBLIC_URL || ''}/logos`;

const getBrandLogoPath = (brand) => {
  const logoFiles = {
    circlek: 'circlek.png',
    gmcirclek: 'circlek.png',
    plungeslagunacirclek: 'circlek.png',
    vvarffcirclek: 'circlek.png',
    viada: 'viada.png',
    orlen: 'orlen.png',
    neste: 'neste.webp',
    nestelietuva: 'neste.webp',
    alausa: 'alausa.jpg',
    emsi: 'emsi.png',
    stateta: 'stateta.png',
    naftrus: 'naftrus.jpg',
    apsaga: 'apsaga.png',
    lukoil: 'lukoil.png',
    gotika: 'gotika.png',
    expres: 'expres.png',
    statoil: 'statoil.png',
    gulf: 'gulf.png',
    tauras: 'tauras.png',
    deglita: 'deglita.png',
    virsi: 'virsi.png',
    saurida: 'saurida.png',
    trevena: 'trevena.png',
    as24lietuva: 'as24.png',
    as24: 'as24.png',
    bpetroleum: 'bpetroleum.png',
    balticpetroleum: 'bpetroleum.png',
    jozita: 'jozita.jfif',
  };

  const normalized = normalizeBrand(brand);

  if (!normalized) return null;

  const directMatch = logoFiles[normalized];
  if (directMatch) return `${LOGO_BASE_PATH}/${directMatch}`;

  if (normalized.includes('circlek')) return `${LOGO_BASE_PATH}/circlek.png`;
  if (normalized.includes('orlen')) return `${LOGO_BASE_PATH}/orlen.png`;
  if (normalized.includes('viada')) return `${LOGO_BASE_PATH}/viada.png`;
  if (normalized.includes('neste')) return `${LOGO_BASE_PATH}/neste.webp`;
  if (normalized.includes('emsi')) return `${LOGO_BASE_PATH}/emsi.png`;
  if (normalized.includes('stateta')) return `${LOGO_BASE_PATH}/stateta.png`;
  if (normalized.includes('naftrus')) return `${LOGO_BASE_PATH}/naftrus.jpg`;
  if (normalized.includes('saurida')) return `${LOGO_BASE_PATH}/saurida.png`;
  if (normalized.includes('trevena')) return `${LOGO_BASE_PATH}/trevena.png`;
  if (normalized.includes('as24')) return `${LOGO_BASE_PATH}/as24.png`;
  if (normalized.includes('petroleum')) return `${LOGO_BASE_PATH}/bpetroleum.png`;
  if (normalized.includes('jozita')) return `${LOGO_BASE_PATH}/jozita.jfif`;

  return null;
};

const BASE_SITE_URL = 'https://kuras123.lt';
const DATA_METHODOLOGY_PATH = '/duomenys-metodika';
const FAQ_PATH = '/duk';
const CITY_PATH_PREFIX = '/miestas';
const BRAND_PATH_PREFIX = '/tinklas';
const ENA_SOURCE_URL = 'https://www.ena.lt/degalu-kainos-degalinese/';

const normalizeForId = (value = '') => {
  return value
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const shortHash = (value = '') => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36).slice(0, 6);
};

const createStationId = (brand, address, city) => {
  const raw = `${brand}|${address}|${city}`;
  const slug = normalizeForId(`${brand}-${city}-${address}`) || 'degaline';
  const hash = shortHash(raw);
  return `${slug}-${hash}`;
};

const getStationIdFromPath = (path) => {
  const match = path.match(/^\/degaline\/([a-z0-9-]+)(?:\/.*)?$/i);
  return match ? match[1].toLowerCase() : null;
};

const getCitySlugFromPath = (path) => {
  const match = path.match(/^\/miestas\/([a-z0-9-]+)(?:\/.*)?$/i);
  return match ? match[1].toLowerCase() : null;
};

const getBrandSlugFromPath = (path) => {
  const match = path.match(/^\/tinklas\/([a-z0-9-]+)(?:\/.*)?$/i);
  return match ? match[1].toLowerCase() : null;
};

const ALERTS_STORAGE_KEY = 'kuras123-price-alerts';
const ALERT_DEVICE_ID_STORAGE_KEY = 'kuras123-alert-device-id';
const ALERT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const ALERT_OPEN_QUERY_PARAM = 'openAlerts';
const MAP_STATION_QUERY_PARAM = 'station';
const MAP_LAT_QUERY_PARAM = 'lat';
const MAP_LNG_QUERY_PARAM = 'lng';
const MAP_ZOOM_QUERY_PARAM = 'zoom';
const MAP_STATION_FOCUS_ZOOM = 15;
const ALERTS_API_BASE_URL = (process.env.REACT_APP_ALERTS_API_BASE_URL || '').replace(/\/$/, '');

const getSystemPanelTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'ocean';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'graphite' : 'ocean';
};

const buildAlertOpenUrl = () => {
  if (typeof window === 'undefined') return '/';
  return `${window.location.origin}/?${ALERT_OPEN_QUERY_PARAM}=1`;
};

const buildStationMapUrl = (station) => {
  if (!station) return '/';

  const params = new URLSearchParams();

  if (station.id) {
    params.set(MAP_STATION_QUERY_PARAM, station.id);
  }

  if (Number.isFinite(station.lat) && Number.isFinite(station.lng)) {
    params.set(MAP_LAT_QUERY_PARAM, station.lat.toFixed(6));
    params.set(MAP_LNG_QUERY_PARAM, station.lng.toFixed(6));
    params.set(MAP_ZOOM_QUERY_PARAM, String(MAP_STATION_FOCUS_ZOOM));
  }

  const query = params.toString();
  return query ? `/?${query}` : '/';
};

const FUEL_LABELS = {
  gas: 'Benzinas 95',
  diesel: 'Dyzelinas',
  naturalGas: 'Dujos'
};

const FAQ_ITEMS = [
  {
    question: 'Kaip dažnai atnaujinamos kuro kainos?',
    answer: 'Duomenys reguliariai atnaujinami iš šaltinio ir automatiškai įkeliami į sistemą. Naujausia importo data pateikiama puslapyje Duomenys ir metodika.'
  },
  {
    question: 'Kaip veikia kainos įspėjimai?',
    answer: 'Galite nustatyti tikslinę kainą degalinių tinklui ir kuro tipui. Kai aptinkama kaina žemesnė arba lygi jūsų ribai, naršyklė parodo pranešimą.'
  },
  {
    question: 'Kodėl kai kurios degalinės rodomos ne visai tikslioje vietoje?',
    answer: 'Jei tikslios koordinatės nerastos, sistema naudoja miesto rezervines koordinates. Tokie atvejai vėliau tikslinami rankiniu būdu.'
  },
  {
    question: 'Ar kainos visada sutampa su faktinėmis degalinėje?',
    answer: 'Kainos pateikiamos pagal viešai paskelbtą informaciją. Realios kainos degalinėje gali skirtis dėl laiko vėlavimo ar šaltinio atnaujinimų.'
  }
];

const getFuelPrice = (station, fuelType) => {
  if (!station) return 0;
  if (fuelType === 'diesel') return station.dieselPrice || 0;
  if (fuelType === 'naturalGas') return station.naturalGasPrice || 0;
  return station.gasPrice || 0;
};

const getBestBrandFuelPrice = (stations, brandKey, fuelType) => {
  if (!brandKey || !Array.isArray(stations) || stations.length === 0) {
    return 0;
  }

  const prices = stations
    .filter((station) => normalizeBrand(station.brand) === brandKey)
    .map((station) => getFuelPrice(station, fuelType))
    .filter((price) => Number.isFinite(price) && price > 0);

  if (prices.length === 0) {
    return 0;
  }

  return Math.min(...prices);
};

const formatPrice = (price) => (Number.isFinite(price) && price > 0 ? `${price.toFixed(3)} €/L` : 'Nėra duomenų');

const getTopStationsByFuel = (stations, fuelType = 'gas', limit = 10) => {
  return [...stations]
    .filter((station) => getFuelPrice(station, fuelType) > 0)
    .sort((a, b) => getFuelPrice(a, fuelType) - getFuelPrice(b, fuelType))
    .slice(0, limit);
};

const createBreadcrumbSchema = (id, items) => ({
  '@type': 'BreadcrumbList',
  '@id': id,
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

const loadStoredPriceAlerts = () => {
  if (typeof window === 'undefined') return [];

  try {
    const rawAlerts = window.localStorage.getItem(ALERTS_STORAGE_KEY);
    if (!rawAlerts) return [];
    const parsedAlerts = JSON.parse(rawAlerts);
    return Array.isArray(parsedAlerts) ? parsedAlerts : [];
  } catch (error) {
    console.warn('Could not load stored price alerts:', error);
    return [];
  }
};

const createFallbackDeviceId = () => {
  return `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateAlertDeviceId = () => {
  if (typeof window === 'undefined') return '';

  try {
    const existing = window.localStorage.getItem(ALERT_DEVICE_ID_STORAGE_KEY);
    if (existing) return existing;

    const deviceId = typeof window.crypto !== 'undefined' && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : createFallbackDeviceId();

    window.localStorage.setItem(ALERT_DEVICE_ID_STORAGE_KEY, deviceId);
    return deviceId;
  } catch (error) {
    console.warn('Could not load or persist alert device ID:', error);
    return createFallbackDeviceId();
  }
};

const getAlertsApiUrl = (path) => {
  return `${ALERTS_API_BASE_URL}${path}`;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const normalized = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const outputArray = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; i += 1) {
    outputArray[i] = raw.charCodeAt(i);
  }

  return outputArray;
};

const formatAlertDateTime = (isoDateTime) => {
  if (!isoDateTime) return '';
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString('lt-LT', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
};

const parseSourceDateToIso = (rawDate = '') => {
  const cleaned = rawDate.trim();
  const isoMatch = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const spacedMatch = cleaned.match(/^(\d{4})\s+(\d{1,2})\s+(\d{1,2})$/);
  if (!spacedMatch) {
    return '';
  }

  const [, year, month, day] = spacedMatch;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const formatSourceDateLt = (isoDate = '') => {
  if (!isoDate) return 'Nenurodyta';

  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }

  return parsed.toLocaleDateString('lt-LT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const normalizeCoordinateLookupText = (value = '') => String(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\u00a0/g, ' ')
  .replace(/\blt[-\s]?\d{5}\b/g, ' ')
  .replace(/\b\d{5}\b/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getAddressLookupVariants = (address = '') => {
  const rawAddress = String(address).replace(/\u00a0/g, ' ').trim();
  const parts = rawAddress.split(',').map((part) => part.trim()).filter(Boolean);
  const variants = new Set([rawAddress]);

  if (parts.length > 1) {
    variants.add(parts.join(' '));
    variants.add(parts.slice(1).join(', '));
    variants.add(parts.slice(0, -1).join(', '));
  }

  if (parts.length > 2) {
    variants.add(parts.slice(1, -1).join(', '));
  }

  return Array.from(variants)
    .map(normalizeCoordinateLookupText)
    .filter(Boolean);
};

const buildCoordinateLookupIndex = (coordsMap = {}) => {
  const index = new Map();

  Object.entries(coordsMap).forEach(([key, coords]) => {
    const separatorIndex = key.indexOf('|');
    if (separatorIndex === -1) return;

    const municipality = key.slice(0, separatorIndex);
    const address = key.slice(separatorIndex + 1);
    const municipalityKey = normalizeCoordinateLookupText(municipality);

    getAddressLookupVariants(address).forEach((addressKey) => {
      const lookupKey = `${municipalityKey}|${addressKey}`;
      if (!index.has(lookupKey)) {
        index.set(lookupKey, coords);
      }
    });
  });

  return index;
};

const findCoordinatesForStation = (coordsMap = {}, coordsIndex = new Map(), municipality = '', address = '') => {
  const exactKey = `${municipality}|${address}`;
  if (coordsMap[exactKey]) {
    return coordsMap[exactKey];
  }

  const municipalityKey = normalizeCoordinateLookupText(municipality);
  for (const addressKey of getAddressLookupVariants(address)) {
    const coords = coordsIndex.get(`${municipalityKey}|${addressKey}`);
    if (coords) {
      return coords;
    }
  }

  return null;
};

const getInitialBestPricesExpanded = () => {
  return false;
};

function App() {
  const [gasStations, setGasStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [visibleStations, setVisibleStations] = useState([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFuel, setSelectedFuel] = useState('gas'); // 'gas', 'diesel', or 'naturalGas'
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showFuelDropdown, setShowFuelDropdown] = useState(false);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [showMobileFiltersPanel, setShowMobileFiltersPanel] = useState(false);
  const [isBestPricesExpanded, setIsBestPricesExpanded] = useState(getInitialBestPricesExpanded);
  const [locateRequestId, setLocateRequestId] = useState(0);
  const [pathname, setPathname] = useState(window.location.pathname);
  const [priceAlerts, setPriceAlerts] = useState(loadStoredPriceAlerts);
  const [alertDeviceId] = useState(getOrCreateAlertDeviceId);
  const [isServerPushReady, setIsServerPushReady] = useState(false);
  const [alertBrandKey, setAlertBrandKey] = useState('');
  const [alertFuelType, setAlertFuelType] = useState('gas');
  const [alertTargetPrice, setAlertTargetPrice] = useState('');
  const [alertFeedback, setAlertFeedback] = useState('');
  const [dataUpdatedDate, setDataUpdatedDate] = useState('');
  const [panelTheme, setPanelTheme] = useState(getInitialPanelTheme);
  const brandFilterRef = useRef(null);
  const fuelFilterRef = useRef(null);
  const alertFilterRef = useRef(null);
  const mobileFiltersRef = useRef(null);
  const mobileActionBarRef = useRef(null);
  const lastAnalyticsPageRef = useRef('');
  const stationRouteId = getStationIdFromPath(pathname);
  const cityRouteSlug = getCitySlugFromPath(pathname);
  const brandRouteSlug = getBrandSlugFromPath(pathname);
  const isStationRoute = stationRouteId !== null;
  const isCityRoute = cityRouteSlug !== null;
  const isBrandRoute = brandRouteSlug !== null;
  const isDataMethodologyRoute = pathname === DATA_METHODOLOGY_PATH;
  const isFaqRoute = pathname === FAQ_PATH;

  const cityPages = useMemo(() => {
    const grouped = new Map();

    gasStations.forEach((station) => {
      const cityName = station.city || '';
      const slug = normalizeForId(cityName);
      if (!slug) return;

      if (!grouped.has(slug)) {
        grouped.set(slug, {
          slug,
          name: cityName,
          stations: []
        });
      }

      grouped.get(slug).stations.push(station);
    });

    return Array.from(grouped.values()).sort((a, b) => a.name.localeCompare(b.name, 'lt'));
  }, [gasStations]);

  const brandPages = useMemo(() => {
    const grouped = new Map();

    gasStations.forEach((station) => {
      const brandName = station.brand || '';
      const slug = normalizeForId(brandName);
      if (!slug) return;

      if (!grouped.has(slug)) {
        grouped.set(slug, {
          slug,
          name: brandName,
          stations: []
        });
      }

      grouped.get(slug).stations.push(station);
    });

    return Array.from(grouped.values()).sort((a, b) => b.stations.length - a.stations.length || a.name.localeCompare(b.name, 'lt'));
  }, [gasStations]);

  const topCityLinkPages = useMemo(() => cityPages.slice(0, 12), [cityPages]);
  const topBrandLinkPages = useMemo(() => brandPages.slice(0, 12), [brandPages]);

  const routeStation = stationRouteId !== null
    ? gasStations.find((station) => station.id === stationRouteId)
    : null;
  const routeCityPage = cityRouteSlug !== null
    ? cityPages.find((cityPage) => cityPage.slug === cityRouteSlug)
    : null;
  const routeBrandPage = brandRouteSlug !== null
    ? brandPages.find((brandPage) => brandPage.slug === brandRouteSlug)
    : null;
  const mapFocusTarget = useMemo(() => {
    if (typeof window === 'undefined' || pathname !== '/') return null;

    const params = new URLSearchParams(window.location.search);
    const requestedStationId = (params.get(MAP_STATION_QUERY_PARAM) || '').toLowerCase();
    const parsedLat = Number.parseFloat(params.get(MAP_LAT_QUERY_PARAM) || '');
    const parsedLng = Number.parseFloat(params.get(MAP_LNG_QUERY_PARAM) || '');
    const parsedZoom = Number.parseInt(params.get(MAP_ZOOM_QUERY_PARAM) || '', 10);
    const zoom = Number.isFinite(parsedZoom) ? parsedZoom : MAP_STATION_FOCUS_ZOOM;

    if (requestedStationId) {
      const stationMatch = gasStations.find((station) => station.id === requestedStationId);
      if (stationMatch && Number.isFinite(stationMatch.lat) && Number.isFinite(stationMatch.lng)) {
        return {
          stationId: stationMatch.id,
          lat: stationMatch.lat,
          lng: stationMatch.lng,
          zoom
        };
      }
    }

    if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) {
      return {
        stationId: requestedStationId || null,
        lat: parsedLat,
        lng: parsedLng,
        zoom
      };
    }

    return null;
  }, [pathname, gasStations]);
  const effectiveDataUpdatedDate = dataUpdatedDate || new Date().toISOString().slice(0, 10);
  const routeStationPageUrl = routeStation ? `${BASE_SITE_URL}/degaline/${routeStation.id}` : '';

  const structuredData = (() => {
    if (!isStationRoute && !isDataMethodologyRoute && !isFaqRoute && !isCityRoute && !isBrandRoute) {
      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${BASE_SITE_URL}/#organization`,
            name: 'kuras123.lt',
            url: `${BASE_SITE_URL}/`,
            logo: `${BASE_SITE_URL}/favicon.svg`
          },
          {
            '@type': 'WebSite',
            '@id': `${BASE_SITE_URL}/#website`,
            url: `${BASE_SITE_URL}/`,
            name: 'kuras123.lt',
            inLanguage: 'lt-LT',
            publisher: {
              '@id': `${BASE_SITE_URL}/#organization`
            }
          }
        ]
      };
    }

    if (isFaqRoute) {
      const faqPageUrl = `${BASE_SITE_URL}${FAQ_PATH}`;
      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'FAQPage',
            '@id': `${faqPageUrl}#faq`,
            url: faqPageUrl,
            inLanguage: 'lt-LT',
            mainEntity: FAQ_ITEMS.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer
              }
            }))
          },
          createBreadcrumbSchema(`${faqPageUrl}#breadcrumb`, [
            { name: 'Pradžia', url: `${BASE_SITE_URL}/` },
            { name: 'DUK', url: faqPageUrl }
          ])
        ]
      };
    }

    if (isDataMethodologyRoute) {
      const methodologyPageUrl = `${BASE_SITE_URL}${DATA_METHODOLOGY_PATH}`;

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${methodologyPageUrl}#webpage`,
            url: methodologyPageUrl,
            name: 'Duomenys ir metodika | kuras123.lt',
            inLanguage: 'lt-LT',
            dateModified: effectiveDataUpdatedDate,
            description: 'Kaip Kuras123 renka ir atnaujina degalų kainų duomenis Lietuvoje: šaltiniai, dažnis, tikslumas ir apribojimai.'
          },
          {
            '@type': 'Dataset',
            '@id': `${methodologyPageUrl}#dataset`,
            name: 'Kuras123 Lietuvos degalinių kainų duomenys',
            description: 'Lietuvos degalinių kainų duomenys, reguliariai atnaujinami iš viešai skelbiamų ENA kuro kainų šaltinių.',
            url: methodologyPageUrl,
            inLanguage: 'lt-LT',
            isBasedOn: ENA_SOURCE_URL,
            creator: {
              '@type': 'Organization',
              name: 'Kuras123',
              url: BASE_SITE_URL
            },
            distribution: {
              '@type': 'DataDownload',
              encodingFormat: 'text/csv',
              contentUrl: `${BASE_SITE_URL}/data.csv`
            },
            dateModified: effectiveDataUpdatedDate,
            mainEntityOfPage: {
              '@id': `${methodologyPageUrl}#webpage`
            }
          },
          createBreadcrumbSchema(`${methodologyPageUrl}#breadcrumb`, [
            { name: 'Pradžia', url: `${BASE_SITE_URL}/` },
            { name: 'Duomenys ir metodika', url: methodologyPageUrl }
          ])
        ]
      };
    }

    if (isCityRoute && routeCityPage) {
      const cityPageUrl = `${BASE_SITE_URL}${CITY_PATH_PREFIX}/${routeCityPage.slug}`;
      const cityItems = getTopStationsByFuel(routeCityPage.stations, 'gas', 10);

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'CollectionPage',
            '@id': `${cityPageUrl}#collection`,
            url: cityPageUrl,
            name: `${routeCityPage.name} degalinių kainos | kuras123.lt`,
            inLanguage: 'lt-LT',
            mainEntity: {
              '@type': 'ItemList',
              itemListOrder: 'https://schema.org/ItemListOrderAscending',
              numberOfItems: cityItems.length,
              itemListElement: cityItems.map((station, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `${BASE_SITE_URL}/degaline/${station.id}`,
                name: `${station.brand} ${station.address}`
              }))
            }
          },
          createBreadcrumbSchema(`${cityPageUrl}#breadcrumb`, [
            { name: 'Pradžia', url: `${BASE_SITE_URL}/` },
            { name: routeCityPage.name, url: cityPageUrl }
          ])
        ]
      };
    }

    if (isBrandRoute && routeBrandPage) {
      const brandPageUrl = `${BASE_SITE_URL}${BRAND_PATH_PREFIX}/${routeBrandPage.slug}`;
      const brandItems = getTopStationsByFuel(routeBrandPage.stations, 'gas', 10);

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'CollectionPage',
            '@id': `${brandPageUrl}#collection`,
            url: brandPageUrl,
            name: `${routeBrandPage.name} degalinių kainos | kuras123.lt`,
            inLanguage: 'lt-LT',
            mainEntity: {
              '@type': 'ItemList',
              itemListOrder: 'https://schema.org/ItemListOrderAscending',
              numberOfItems: brandItems.length,
              itemListElement: brandItems.map((station, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `${BASE_SITE_URL}/degaline/${station.id}`,
                name: `${station.brand} ${station.address}`
              }))
            }
          },
          createBreadcrumbSchema(`${brandPageUrl}#breadcrumb`, [
            { name: 'Pradžia', url: `${BASE_SITE_URL}/` },
            { name: routeBrandPage.name, url: brandPageUrl }
          ])
        ]
      };
    }

    if (isStationRoute && routeStation) {
      const stationBrandUrl = `${BASE_SITE_URL}${BRAND_PATH_PREFIX}/${normalizeForId(routeStation.brand)}`;
      const offers = [
        { name: 'Benzinas 95', price: routeStation.gasPrice },
        { name: 'Dyzelinas', price: routeStation.dieselPrice },
        { name: 'Dujos', price: routeStation.naturalGasPrice }
      ]
        .filter(({ price }) => Number.isFinite(price) && price > 0)
        .map(({ name, price }) => ({
          '@type': 'Offer',
          priceCurrency: 'EUR',
          price: price.toFixed(3),
          availability: 'https://schema.org/InStock',
          itemOffered: {
            '@type': 'Product',
            name
          }
        }));

      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'GasStation',
            '@id': `${routeStationPageUrl}#station`,
            url: routeStationPageUrl,
            name: `${routeStation.brand} ${routeStation.address}`,
            brand: {
              '@type': 'Brand',
              name: routeStation.brand
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: routeStation.address,
              addressLocality: routeStation.city,
              addressCountry: 'LT'
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: routeStation.lat,
              longitude: routeStation.lng
            },
            areaServed: {
              '@type': 'Country',
              name: 'Lietuva'
            },
            ...(offers.length > 0 ? { makesOffer: offers } : {})
          },
          createBreadcrumbSchema(`${routeStationPageUrl}#breadcrumb`, [
            { name: 'Pradžia', url: `${BASE_SITE_URL}/` },
            { name: routeStation.brand, url: stationBrandUrl },
            { name: `${routeStation.brand} ${routeStation.address}`, url: routeStationPageUrl }
          ])
        ]
      };
    }

    return null;
  })();

  const openAlertsPanelFromNotification = () => {
    setShowAlertPanel(true);
    setShowBrandDropdown(false);
    setShowFuelDropdown(false);
    setShowMobileFiltersPanel(false);
    setIsBestPricesExpanded(false);
  };

  const fuelOptions = [
    { value: 'gas', label: '⛽ Benzinas 95' },
    { value: 'diesel', label: '🚛 Dyzelinas' },
    { value: 'naturalGas', label: '🌿 Dujos' }
  ];

  const alertBrandOptions = Object.entries(gasStations.reduce((acc, station) => {
    if (station.brand) {
      acc[station.brand] = (acc[station.brand] || 0) + 1;
    }
    return acc;
  }, {}))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'lt'))
    .map(([name, count]) => ({ name, count, key: normalizeBrand(name) }));

  const handleSelectBrandFilter = (brandName, source) => {
    setSelectedBrand(brandName);
    setShowBrandDropdown(false);
    setShowMobileFiltersPanel(false);
    logAnalyticsEvent('select_brand_filter', {
      brand: brandName,
      source,
      is_all: brandName === 'all'
    });
  };

  const handleSelectFuelFilter = (fuelType, source) => {
    setSelectedFuel(fuelType);
    setShowFuelDropdown(false);
    setShowMobileFiltersPanel(false);
    logAnalyticsEvent('select_fuel_filter', {
      fuel_type: fuelType,
      source
    });
  };

  const handleToggleBrandDropdown = () => {
    setShowBrandDropdown((prev) => {
      const next = !prev;
      logAnalyticsEvent('toggle_filter_panel', {
        panel: 'brand',
        expanded: next,
        source: 'desktop'
      });
      if (next) {
        setShowFuelDropdown(false);
        setShowAlertPanel(false);
        setShowMobileFiltersPanel(false);
        setIsBestPricesExpanded(false);
      }
      return next;
    });
  };

  const handleToggleFuelDropdown = () => {
    setShowFuelDropdown((prev) => {
      const next = !prev;
      logAnalyticsEvent('toggle_filter_panel', {
        panel: 'fuel',
        expanded: next,
        source: 'desktop'
      });
      if (next) {
        setShowBrandDropdown(false);
        setShowAlertPanel(false);
        setShowMobileFiltersPanel(false);
        setIsBestPricesExpanded(false);
      }
      return next;
    });
  };

  const handleToggleAlertPanel = () => {
    setShowAlertPanel((prev) => {
      const next = !prev;
      logAnalyticsEvent('toggle_alerts_panel', {
        expanded: next
      });
      if (next) {
        setShowBrandDropdown(false);
        setShowFuelDropdown(false);
        setShowMobileFiltersPanel(false);
        setIsBestPricesExpanded(false);
      }
      return next;
    });
  };

  const handleToggleMobileFiltersPanel = () => {
    setShowMobileFiltersPanel((prev) => {
      const next = !prev;
      logAnalyticsEvent('toggle_filter_panel', {
        panel: 'mobile_filters',
        expanded: next,
        source: 'mobile'
      });
      if (next) {
        setShowBrandDropdown(false);
        setShowFuelDropdown(false);
        setShowAlertPanel(false);
        setIsBestPricesExpanded(false);
      }
      return next;
    });
  };

  const handleToggleBestPricesPanel = () => {
    setIsBestPricesExpanded((prev) => {
      const next = !prev;
      logAnalyticsEvent('toggle_best_prices_panel', {
        expanded: next,
        source: 'mobile_action_bar',
        visible_station_count: visibleStations.length
      });
      if (next) {
        setShowBrandDropdown(false);
        setShowFuelDropdown(false);
        setShowAlertPanel(false);
        setShowMobileFiltersPanel(false);
      }
      return next;
    });
  };

  const handleLocateUser = () => {
    logAnalyticsEvent('request_user_location', {
      source: 'mobile_action_bar'
    });
    setLocateRequestId((prev) => prev + 1);
    setShowBrandDropdown(false);
    setShowFuelDropdown(false);
    setShowAlertPanel(false);
    setShowMobileFiltersPanel(false);
    setIsBestPricesExpanded(false);
  };

  const handleBestPricesExpandedChange = (expanded, source = 'best_prices_panel') => {
    logAnalyticsEvent('toggle_best_prices_panel', {
      expanded,
      source,
      visible_station_count: visibleStations.length
    });
    setIsBestPricesExpanded(expanded);
    if (expanded) {
      setShowBrandDropdown(false);
      setShowFuelDropdown(false);
      setShowAlertPanel(false);
      setShowMobileFiltersPanel(false);
    }
  };

  const handleLocationResult = useCallback((result = {}) => {
    logAnalyticsEvent('user_location_result', {
      status: result.status,
      error_message: result.message
    });
  }, []);

  const handleStationPopupOpen = useCallback((station) => {
    logAnalyticsEvent('station_popup_open', {
      station_id: station.id,
      brand: station.brand,
      city: station.city,
      fuel_type: selectedFuel
    });
  }, [selectedFuel]);

  const handleStationDetailLinkClick = useCallback((station, source) => {
    logAnalyticsEvent('station_detail_click', {
      station_id: station.id,
      brand: station.brand,
      city: station.city,
      source
    });
  }, []);

  const loadDataFromCSV = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    const cacheBust = Date.now();
    
    try {
      console.log('Loading CSV from public folder...');
      const response = await fetch(`/data.csv?v=${cacheBust}`, {
        cache: 'no-store'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const csvText = await response.text();
      console.log('CSV loaded successfully, size:', csvText.length);
      await parseCSVData(csvText, cacheBust, { silent });
    } catch (err) {
      console.error('Error loading CSV:', err);
      setError('Nepavyko įkelti duomenų iš CSV failo. Patikrinkite, ar failas egzistuoja.');
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const parseCSVData = async (csvText, cacheBust = Date.now(), { silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const lines = csvText.split('\n');
      const stations = [];
      let latestSourceDate = '';
      let startLine = 0;
      let csvFormat = 'wide';

      for (let i = 0; i < lines.length; i++) {
        const normalizedLine = lines[i].toLowerCase();
        if (normalizedLine.includes('degalų tipas') && normalizedLine.includes('kaina') && normalizedLine.includes('savivaldyb')) {
          startLine = i + 1;
          csvFormat = 'fuelRows';
          break;
        }
        if (lines[i].includes('Data;') && lines[i].includes('Įmonė')) {
          startLine = i + 1;
          csvFormat = 'wide';
          break;
        }
      }

      const delimiter = csvText.includes(';') ? ';' : ',';
      console.log(`Using delimiter: "${delimiter}", format: ${csvFormat}, starting from line ${startLine}`);

      // Load coordinates from server-side JSON file
      console.log('Loading coordinates from server...');
      let coordsMap = {};
      let coordsIndex = new Map();
      try {
        const coordsResponse = await fetch(`/coordinates.json?v=${cacheBust}`, {
          cache: 'no-store'
        });
        if (coordsResponse.ok) {
          coordsMap = await coordsResponse.json();
          coordsIndex = buildCoordinateLookupIndex(coordsMap);
          console.log(`Loaded ${Object.keys(coordsMap).length} coordinates from server`);
        } else {
          console.warn('Could not load coordinates.json, will use city fallbacks');
        }
      } catch (e) {
        console.warn('Failed to load coordinates.json:', e);
      }

      const parsePrice = (value = '') => {
        const parsed = parseFloat(String(value).replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : 0;
      };
      const stationMap = new Map();
      const upsertStation = (brand, municipality, address) => {
        const key = `${brand}|${municipality}|${address}`;
        if (!stationMap.has(key)) {
          stationMap.set(key, {
            brand,
            municipality,
            address,
            gasPrice: 0,
            dieselPrice: 0,
            naturalGasPrice: 0,
          });
        }
        return stationMap.get(key);
      };

      for (let i = startLine; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const row = line.split(delimiter).map(cell => cell.trim());
        if (row.length < 5) continue;

        if (csvFormat === 'fuelRows') {
          const parsedSourceDate = parseSourceDateToIso(row[5] || '');
          if (parsedSourceDate && (!latestSourceDate || parsedSourceDate > latestSourceDate)) {
            latestSourceDate = parsedSourceDate;
          }

          const brand = row[0] || '';
          const municipality = row[1] || '';
          const address = row[2] || '';
          const fuelType = (row[3] || '').toLowerCase();
          const price = parsePrice(row[4] || '');

          if (!brand || !municipality || !address || price <= 0) continue;

          const station = upsertStation(brand, municipality, address);
          if (fuelType.includes('dyzel')) {
            station.dieselPrice = price;
          } else if (fuelType.includes('snd') || fuelType.includes('duj')) {
            station.naturalGasPrice = price;
          } else if (fuelType.includes('95') || fuelType.includes('benz')) {
            station.gasPrice = price;
          }
          continue;
        }

        const parsedSourceDate = parseSourceDateToIso(row[0] || '');
        if (parsedSourceDate && (!latestSourceDate || parsedSourceDate > latestSourceDate)) {
          latestSourceDate = parsedSourceDate;
        }

        const brand = row[1] || '';
        const municipality = row[2] || '';
        const address = row[3] || '';
        const gasPrice = parsePrice(row[4] || '');
        const dieselPrice = parsePrice(row[5] || '');
        const naturalGasPrice = parsePrice(row[6] || '');

        if (!brand || !address || (gasPrice === 0 && dieselPrice === 0 && naturalGasPrice === 0)) continue;

        const station = upsertStation(brand, municipality, address);
        station.gasPrice = gasPrice;
        station.dieselPrice = dieselPrice;
        station.naturalGasPrice = naturalGasPrice;
      }

      Array.from(stationMap.values()).forEach((station) => {
        const { brand, municipality, address, gasPrice, dieselPrice, naturalGasPrice } = station;
        let coords = findCoordinatesForStation(coordsMap, coordsIndex, municipality, address);
        
        if (!coords) {
          // Fallback to city coordinates
          coords = getCityCoordinates(municipality);
          console.log(`Using city fallback for: ${address}, ${municipality}`);
        } else {
          // Remove fallback flag and reason from coordinates (only used for geocoding script)
          coords = { lat: coords.lat, lng: coords.lng };
        }

        // Validate coordinates are valid numbers
        if (coords && coords.lat && coords.lng && 
            !isNaN(coords.lat) && !isNaN(coords.lng) &&
            coords.lat >= 53 && coords.lat <= 57 &&  // Lithuania latitude range
            coords.lng >= 20 && coords.lng <= 27) {  // Lithuania longitude range
          const stationId = createStationId(brand, address, municipality);
          stations.push({
            id: stationId,
            brand: brand,
            address: address,
            city: municipality,
            gasPrice: gasPrice,
            dieselPrice: dieselPrice,
            naturalGasPrice: naturalGasPrice,
            lat: coords.lat,
            lng: coords.lng,
          });
        } else {
          console.warn(`Invalid coordinates for: ${address}, ${municipality}`, coords);
        }
      });

      console.log(`Successfully loaded ${stations.length} stations`);
      setGasStations(stations);
      setFilteredStations(stations);
      setDataUpdatedDate(latestSourceDate || new Date().toISOString().slice(0, 10));
      if (!silent) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error parsing CSV file:', error);
      setError('Klaida skaitant CSV failą. Patikrinkite failo formatą.');
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const requestNotificationPermissionIfNeeded = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported';
    }

    if (Notification.permission === 'default') {
      try {
        return await Notification.requestPermission();
      } catch (error) {
        console.warn('Notification permission request failed:', error);
        return Notification.permission;
      }
    }

    return Notification.permission;
  };

  const registerPushSubscriptionWithServer = async () => {
    if (
      typeof window === 'undefined' ||
      !alertDeviceId ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      return { ok: false, reason: 'unsupported' };
    }

    try {
      const configResponse = await fetch(getAlertsApiUrl('/api/push/public-key'), {
        cache: 'no-store'
      });

      if (!configResponse.ok) {
        return { ok: false, reason: 'config_unavailable' };
      }

      const config = await configResponse.json();
      if (!config.enabled || !config.publicKey) {
        return { ok: false, reason: 'push_disabled' };
      }

      const registration = await navigator.serviceWorker.ready;
      if (!registration || !registration.pushManager) {
        return { ok: false, reason: 'unsupported' };
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(config.publicKey)
        });
      }

      const registerResponse = await fetch(getAlertsApiUrl('/api/push/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: alertDeviceId,
          subscription
        })
      });

      if (!registerResponse.ok) {
        return { ok: false, reason: 'register_failed' };
      }

      setIsServerPushReady(true);
      return { ok: true };
    } catch (error) {
      console.warn('Push subscription registration failed:', error);
      return { ok: false, reason: 'register_failed' };
    }
  };

  const showPriceAlertNotification = (alert, station, currentPrice) => {
    if (isServerPushReady) {
      return;
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    if (Notification.permission !== 'granted') {
      return;
    }

    const fuelLabel = FUEL_LABELS[alert.fuelType] || 'Kuras';
    const brandLabel = alert.brandLabel || station.brand;
    const title = 'Kuras123: kainos įspėjimas';
    const targetUrl = buildAlertOpenUrl();
    const options = {
      body: `${brandLabel}: ${fuelLabel} kaina ${station.address} dabar ${currentPrice.toFixed(3)} €/L (tikslas ≤ ${alert.targetPrice.toFixed(3)} €/L).`,
      icon: `${window.location.origin}/favicon.svg`,
      tag: `price-alert-${alert.brandKey}-${alert.fuelType}`,
      data: { url: targetUrl }
    };

    const showWindowNotificationFallback = () => {
      const notification = new Notification(title, options);

      notification.onclick = () => {
        notification.close();

        try {
          window.focus();
        } catch (error) {
          console.warn('Could not focus window after notification click:', error);
        }

        const isOnMapHome = window.location.pathname === '/' && !window.location.search;
        if (!isOnMapHome) {
          window.location.assign(targetUrl);
          return;
        }

        openAlertsPanelFromNotification();
      };
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          if (registration && typeof registration.showNotification === 'function') {
            return registration.showNotification(title, options);
          }

          showWindowNotificationFallback();
          return null;
        })
        .catch((error) => {
          console.warn('Service worker notification failed, using window notification fallback:', error);
          showWindowNotificationFallback();
        });
      return;
    }

    showWindowNotificationFallback();
  };

  const handleCreatePriceAlert = async (event) => {
    event.preventDefault();

    if (!alertBrandKey) {
      setAlertFeedback('Pasirinkite degalinių tinklą.');
      return;
    }

    const parsedTargetPrice = Number.parseFloat(alertTargetPrice.replace(',', '.'));
    if (!Number.isFinite(parsedTargetPrice) || parsedTargetPrice <= 0) {
      setAlertFeedback('Įveskite teisingą tikslinę kainą, pvz. 1.399.');
      return;
    }

    const fuelLabel = FUEL_LABELS[alertFuelType] || 'kuro';
    const normalizedTargetPrice = Number(parsedTargetPrice.toFixed(3));
    const selectedBrandOption = alertBrandOptions.find((brand) => brand.key === alertBrandKey);
    const selectedBrandLabel = selectedBrandOption?.name || '';

    if (!selectedBrandLabel) {
      setAlertFeedback('Nepavyko nustatyti degalinių tinklo. Bandykite dar kartą.');
      return;
    }

    const newAlert = {
      id: `${alertBrandKey}-${alertFuelType}-${Date.now()}`,
      brandKey: alertBrandKey,
      brandLabel: selectedBrandLabel,
      fuelType: alertFuelType,
      targetPrice: normalizedTargetPrice,
      active: true,
      createdAt: new Date().toISOString(),
      triggeredAt: ''
    };

    setPriceAlerts((previousAlerts) => {
      const withoutSameFuelAlert = previousAlerts.filter((alert) => !(alert.brandKey === newAlert.brandKey && alert.fuelType === newAlert.fuelType));
      return [...withoutSameFuelAlert, newAlert];
    });

    logAnalyticsEvent('create_price_alert', {
      brand: selectedBrandLabel,
      brand_key: alertBrandKey,
      fuel_type: alertFuelType,
      target_price: normalizedTargetPrice
    });

    const permission = await requestNotificationPermissionIfNeeded();
    if (permission === 'denied') {
      setAlertFeedback(`Įspėjimas išsaugotas, bet naršyklės pranešimai užblokuoti. Leiskite pranešimus, kad gautumėte įspėjimą.`);
    } else if (permission === 'unsupported') {
      setAlertFeedback('Įspėjimas išsaugotas. Ši naršyklė nepalaiko sisteminių pranešimų.');
    } else {
      const registerResult = await registerPushSubscriptionWithServer();
      if (!registerResult.ok && registerResult.reason !== 'push_disabled' && registerResult.reason !== 'config_unavailable') {
        setAlertFeedback(`Įspėjimas sukurtas ${selectedBrandLabel} tinklui: ${fuelLabel} ≤ ${normalizedTargetPrice.toFixed(3)} €/L. Foniniai pranešimai šiame įrenginyje nebuvo pilnai suaktyvinti.`);
      } else {
        setAlertFeedback(`Įspėjimas sukurtas ${selectedBrandLabel} tinklui: ${fuelLabel} ≤ ${normalizedTargetPrice.toFixed(3)} €/L.`);
      }
    }

    setAlertTargetPrice('');
  };

  const handleDeletePriceAlert = (alertId) => {
    const deletedAlert = priceAlerts.find((alert) => alert.id === alertId);
    logAnalyticsEvent('delete_price_alert', {
      brand: deletedAlert?.brandLabel,
      brand_key: deletedAlert?.brandKey,
      fuel_type: deletedAlert?.fuelType,
      active: deletedAlert?.active
    });
    setPriceAlerts((previousAlerts) => previousAlerts.filter((alert) => alert.id !== alertId));
  };

  const getCityCoordinates = (municipality) => {
    // Extract city name from municipality
    // Handle formats like "Kauno m. sav.", "Vilniaus m. sav.", "Utenos r. sav."
    let city = municipality
      .replace(/\s+m\.\s+sav\./i, '')  // Remove "m. sav." (city municipality)
      .replace(/\s+r\.\s+sav\./i, '')  // Remove "r. sav." (district municipality)
      .replace(/\s+sav\./i, '')        // Remove "sav." (municipality)
      .trim();
    
    // Handle genitive case (e.g., "Kauno" -> "Kaunas", "Vilniaus" -> "Vilnius")
    const genitiveMap = {
      'Kauno': 'Kaunas',
      'Vilniaus': 'Vilnius',
      'Klaipėdos': 'Klaipėda',
      'Šiaulių': 'Šiauliai',
      'Panevėžio': 'Panevėžys',
      'Alytaus': 'Alytus',
      'Marijampolės': 'Marijampolė',
      'Mažeikių': 'Mažeikiai',
      'Jonavos': 'Jonava',
      'Utenos': 'Utena',
      'Kėdainių': 'Kėdainiai',
      'Telšių': 'Telšiai',
      'Tauragės': 'Tauragė',
      'Ukmergės': 'Ukmergė',
      'Visagino': 'Visaginas',
      'Plungės': 'Plungė',
      'Kretingos': 'Kretinga',
      'Šilutės': 'Šilutė',
      'Radviliškio': 'Radviliškis',
      'Palangos': 'Palanga',
      'Gargždų': 'Gargždai',
      'Druskininkų': 'Druskininkai',
      'Rokiškio': 'Rokiškis',
      'Biržų': 'Biržai',
      'Elektrėnų': 'Elektrėnai',
      'Garliavos': 'Garliava',
      'Anykščių': 'Anykščiai',
      'Širvintų': 'Širvintos',
      'Šakių': 'Šakiai',
      'Šalčininkų': 'Šalčininkai',
      'Kalvarijos': 'Kalvarija',
      'Ignalinos': 'Ignalina'
    };
    
    // Check if it's in genitive case
    if (genitiveMap[city]) {
      city = genitiveMap[city];
    }
    
    // Direct lookup
    if (CITY_COORDS[city]) {
      return CITY_COORDS[city];
    }
    
    // Try to find partial match (city name contained in municipality or vice versa)
    for (const [mapCity, coords] of Object.entries(CITY_COORDS)) {
      if (city.toLowerCase().includes(mapCity.toLowerCase()) || 
          mapCity.toLowerCase().includes(city.toLowerCase())) {
        return coords;
      }
    }
    
    // If still not found, use the largest city as fallback (Vilnius)
    console.warn(`No coordinates found for municipality: "${municipality}" (parsed as: "${city}") - using Vilnius as fallback`);
    return CITY_COORDS['Vilnius'];
  };


  useEffect(() => {
    loadDataFromCSV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const intervalId = window.setInterval(() => {
      loadDataFromCSV({ silent: true });
    }, ALERT_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ALERTS_STORAGE_KEY, JSON.stringify(priceAlerts));
    } catch (error) {
      console.warn('Could not persist price alerts:', error);
    }
  }, [priceAlerts]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    registerPushSubscriptionWithServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertDeviceId]);

  useEffect(() => {
    if (!isServerPushReady || !alertDeviceId) return;

    const payloadAlerts = priceAlerts
      .filter((alert) => alert.brandKey)
      .map((alert) => ({
        id: alert.id,
        brandKey: alert.brandKey,
        brandLabel: alert.brandLabel,
        fuelType: alert.fuelType,
        targetPrice: alert.targetPrice,
        active: alert.active,
        createdAt: alert.createdAt,
        triggeredAt: alert.triggeredAt,
        triggeredPrice: alert.triggeredPrice,
        triggeredStationAddress: alert.triggeredStationAddress,
        triggeredStationCity: alert.triggeredStationCity
      }));

    fetch(getAlertsApiUrl('/api/alerts/sync'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        deviceId: alertDeviceId,
        alerts: payloadAlerts
      })
    }).catch((error) => {
      console.warn('Could not sync alerts to backend:', error);
    });
  }, [alertDeviceId, isServerPushReady, priceAlerts]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const syncSystemTheme = () => {
      const nextSystemTheme = mediaQuery.matches ? 'graphite' : 'ocean';
      setPanelTheme((currentTheme) => {
        if (currentTheme !== 'ocean' && currentTheme !== 'graphite') {
          return currentTheme;
        }

        return nextSystemTheme;
      });
    };

    syncSystemTheme();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncSystemTheme);
      return () => mediaQuery.removeEventListener('change', syncSystemTheme);
    }

    mediaQuery.addListener(syncSystemTheme);
    return () => mediaQuery.removeListener(syncSystemTheme);
  }, []);

  useEffect(() => {
    if (!gasStations.length) return;

    setPriceAlerts((previousAlerts) => {
      let hasMigrationUpdates = false;

      const migratedAlerts = previousAlerts.map((alert) => {
        if (alert.brandKey) {
          return alert;
        }

        if (alert.stationId) {
          const station = gasStations.find((item) => item.id === alert.stationId);
          if (station) {
            hasMigrationUpdates = true;
            return {
              ...alert,
              brandKey: normalizeBrand(station.brand),
              brandLabel: station.brand
            };
          }
        }

        return alert;
      });

      return hasMigrationUpdates ? migratedAlerts : previousAlerts;
    });
  }, [gasStations]);

  useEffect(() => {
    if (!gasStations.length || !priceAlerts.length) return;

    setPriceAlerts((previousAlerts) => {
      let hasUpdates = false;

      const nextAlerts = previousAlerts.map((alert) => {
        if (!alert.active) return alert;

        if (!alert.brandKey) return alert;

        const brandStations = gasStations.filter((item) => normalizeBrand(item.brand) === alert.brandKey);
        if (!brandStations.length) {
          return alert;
        }

        let matchedStation = null;
        let matchedPrice = Infinity;

        brandStations.forEach((station) => {
          const currentPrice = getFuelPrice(station, alert.fuelType);
          if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
            return;
          }

          if (currentPrice <= alert.targetPrice && currentPrice < matchedPrice) {
            matchedStation = station;
            matchedPrice = currentPrice;
          }
        });

        if (matchedStation) {
          hasUpdates = true;
          showPriceAlertNotification(alert, matchedStation, matchedPrice);
          return {
            ...alert,
            active: false,
            triggeredAt: new Date().toISOString(),
            triggeredPrice: matchedPrice,
            triggeredStationAddress: matchedStation.address,
            triggeredStationCity: matchedStation.city
          };
        }

        return alert;
      });

      return hasUpdates ? nextAlerts : previousAlerts;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gasStations, priceAlerts]);

  useEffect(() => {
    if (!alertBrandOptions.length) return;

    const hasSelectedBrand = alertBrandOptions.some((brand) => brand.key === alertBrandKey);
    if (!hasSelectedBrand) {
      setAlertBrandKey(alertBrandOptions[0].key);
    }
  }, [alertBrandOptions, alertBrandKey]);

  useEffect(() => {
    setAlertFeedback('');
  }, [stationRouteId, alertBrandKey]);

  useEffect(() => {
    if (gasStations.length > 0) {
      const stationsInView = mapBounds
        ? gasStations.filter((station) => (
            station.lat >= mapBounds.south &&
            station.lat <= mapBounds.north &&
            station.lng >= mapBounds.west &&
            station.lng <= mapBounds.east
          ))
        : gasStations;

      const brandCounts = stationsInView.reduce((acc, station) => {
        if (station.brand) {
          acc[station.brand] = (acc[station.brand] || 0) + 1;
        }
        return acc;
      }, {});

      const sortedBrands = Object.entries(brandCounts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'lt'))
        .map(([name, count]) => ({ name, count }));

      setBrands(sortedBrands);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gasStations, mapBounds]);

  useEffect(() => {
    let filtered = gasStations;
    
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(s => s.brand === selectedBrand);
    }
    
    setFilteredStations(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand, gasStations]);

  useEffect(() => {
    if (mapBounds && filteredStations.length > 0) {
      const visible = filteredStations.filter(station => {
        return station.lat >= mapBounds.south &&
               station.lat <= mapBounds.north &&
               station.lng >= mapBounds.west &&
               station.lng <= mapBounds.east;
      });
      setVisibleStations(visible);
    } else {
      setVisibleStations(filteredStations);
    }
  }, [mapBounds, filteredStations]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const isInsideMobileActionBar = mobileActionBarRef.current && mobileActionBarRef.current.contains(event.target);

      if (!isInsideMobileActionBar && brandFilterRef.current && !brandFilterRef.current.contains(event.target)) {
        setShowBrandDropdown(false);
      }

      if (!isInsideMobileActionBar && fuelFilterRef.current && !fuelFilterRef.current.contains(event.target)) {
        setShowFuelDropdown(false);
      }

      if (!isInsideMobileActionBar && alertFilterRef.current && !alertFilterRef.current.contains(event.target)) {
        setShowAlertPanel(false);
      }

      if (!isInsideMobileActionBar && mobileFiltersRef.current && !mobileFiltersRef.current.contains(event.target)) {
        setShowMobileFiltersPanel(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    const handlePopstate = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const pagePath = `${window.location.pathname}${window.location.search || ''}`;
    if (lastAnalyticsPageRef.current === pagePath) return;

    lastAnalyticsPageRef.current = pagePath;
    logPageView(pagePath);
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get(ALERT_OPEN_QUERY_PARAM) !== '1') return;

    if (window.location.pathname === '/') {
      openAlertsPanelFromNotification();
    }

    params.delete(ALERT_OPEN_QUERY_PARAM);
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [pathname]);

  useEffect(() => {
    const setMetaContent = (selector, content) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    const setCanonical = (href) => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', href);
      }
    };

    const defaultTitle = 'Degalinių kainos Lietuvoje | kuras123.lt';
    const defaultDescription = 'Interaktyvus Lietuvos degalinių žemėlapis su kainomis. Palyginkite benzino, dyzelino ir dujų kainas pagal miestą bei degalinių tinklą.';
    const defaultKeywords = 'degalinių kainos, benzino kainos, dyzelino kainos, dujų kainos, kuro kainos, Lietuva, pigiausios degalinės';

    let title = defaultTitle;
    let description = defaultDescription;
    let keywords = defaultKeywords;
    let pageUrl = `${BASE_SITE_URL}/`;

    if (isDataMethodologyRoute) {
      title = 'Duomenys ir metodika | kuras123.lt';
      description = dataUpdatedDate
        ? `Kaip Kuras123 renka ir atnaujina degalų kainų duomenis Lietuvoje. Paskutinio importo data: ${dataUpdatedDate}.`
        : 'Kaip Kuras123 renka ir atnaujina degalų kainų duomenis Lietuvoje: šaltiniai, dažnis, tikslumas ir apribojimai.';
      keywords = 'kuro kainų duomenys, degalų kainų šaltinis, metodika, duomenų atnaujinimas, kuras123';
      pageUrl = `${BASE_SITE_URL}${DATA_METHODOLOGY_PATH}`;
    } else if (isFaqRoute) {
      title = 'DUK apie kuro kainas | kuras123.lt';
      description = 'Dažniausiai užduodami klausimai apie kuro kainų atnaujinimą, įspėjimus, žemėlapio duomenų tikslumą ir naudojimą.';
      keywords = 'duk, kuro kainos, benzino kainos, dyzelino kainos, kainų įspėjimai, kuras123';
      pageUrl = `${BASE_SITE_URL}${FAQ_PATH}`;
    } else if (isCityRoute && routeCityPage) {
      title = `${routeCityPage.name} degalinių kainos | kuras123.lt`;
      description = `${routeCityPage.name} degalinių kainų suvestinė. Palyginkite benzino, dyzelino ir dujų kainas ${routeCityPage.stations.length} degalinėse.`;
      keywords = `${routeCityPage.name} degalinių kainos, benzino kaina ${routeCityPage.name}, dyzelino kaina ${routeCityPage.name}, kuro kainos`;
      pageUrl = `${BASE_SITE_URL}${CITY_PATH_PREFIX}/${routeCityPage.slug}`;
    } else if (isCityRoute && !loading) {
      title = 'Miestas nerastas | kuras123.lt';
      description = 'Nurodytam miestui degalinių duomenų nerasta. Peržiūrėkite visų degalinių kainas žemėlapyje.';
      pageUrl = `${BASE_SITE_URL}${pathname}`;
    } else if (isBrandRoute && routeBrandPage) {
      title = `${routeBrandPage.name} degalinių kainos | kuras123.lt`;
      description = `${routeBrandPage.name} degalinių kainų suvestinė. Palyginkite benzino, dyzelino ir dujų kainas visame tinkle.`;
      keywords = `${routeBrandPage.name} degalinių kainos, benzino kaina ${routeBrandPage.name}, dyzelino kaina ${routeBrandPage.name}, kuro kainos`;
      pageUrl = `${BASE_SITE_URL}${BRAND_PATH_PREFIX}/${routeBrandPage.slug}`;
    } else if (isBrandRoute && !loading) {
      title = 'Degalinių tinklas nerastas | kuras123.lt';
      description = 'Nurodytam degalinių tinklui duomenų nerasta. Peržiūrėkite visų degalinių kainas žemėlapyje.';
      pageUrl = `${BASE_SITE_URL}${pathname}`;
    } else if (isStationRoute && routeStation) {
      title = `${routeStation.brand} ${routeStation.address}, ${routeStation.city} | Kuro kainos | kuras123.lt`;
      description = `${routeStation.brand} degalinė ${routeStation.address}, ${routeStation.city}. Benzinas: ${routeStation.gasPrice > 0 ? `${routeStation.gasPrice.toFixed(3)} €/L` : 'nėra duomenų'}, dyzelinas: ${routeStation.dieselPrice > 0 ? `${routeStation.dieselPrice.toFixed(3)} €/L` : 'nėra duomenų'}.`;
      keywords = `${routeStation.brand} ${routeStation.city}, degalinė ${routeStation.city}, benzino kaina ${routeStation.city}, dyzelino kaina ${routeStation.city}, kuro kainos`;
      pageUrl = `${BASE_SITE_URL}/degaline/${routeStation.id}`;
    } else if (isStationRoute && !loading) {
      title = 'Degalinė nerasta | kuras123.lt';
      description = 'Nurodyta degalinė nerasta. Peržiūrėkite visų degalinių kainas Lietuvos žemėlapyje.';
      pageUrl = `${BASE_SITE_URL}${pathname}`;
    }

    document.title = title;
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[name="keywords"]', keywords);
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="og:url"]', pageUrl);
    setMetaContent('meta[name="twitter:title"]', title);
    setMetaContent('meta[name="twitter:description"]', description);
    setCanonical(pageUrl);
  }, [isDataMethodologyRoute, isFaqRoute, isCityRoute, isBrandRoute, isStationRoute, routeCityPage, routeBrandPage, routeStation, pathname, loading, dataUpdatedDate]);

  const renderEntityNavigationLinks = ({ title = 'Naršykite pagal miestą ir tinklą', cityLimit = 12, brandLimit = 12 } = {}) => {
    const cityLinks = topCityLinkPages.slice(0, cityLimit);
    const brandLinks = topBrandLinkPages.slice(0, brandLimit);

    if (cityLinks.length === 0 && brandLinks.length === 0) {
      return null;
    }

    return (
      <section className="info-section discover-links-section">
        <h2>{title}</h2>

        {cityLinks.length > 0 && (
          <div className="discover-links-group">
            <h3>Miestai</h3>
            <div className="discover-links-grid">
              {cityLinks.map((cityPage) => (
                <a key={cityPage.slug} className="discover-link-chip" href={`${CITY_PATH_PREFIX}/${cityPage.slug}`}>
                  {cityPage.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {brandLinks.length > 0 && (
          <div className="discover-links-group">
            <h3>Tinklai</h3>
            <div className="discover-links-grid">
              {brandLinks.map((brandPage) => (
                <a key={brandPage.slug} className="discover-link-chip" href={`${BRAND_PATH_PREFIX}/${brandPage.slug}`}>
                  {brandPage.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  };

  const renderStationDetail = () => {
    if (!routeStation) {
      return (
        <div className="empty-state">
          <MapPin size={64} className="empty-icon" />
          <h2>Degalinė nerasta</h2>
          <p>Patikrinkite nuorodą arba grįžkite į bendrą žemėlapį.</p>
          <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        </div>
      );
    }

    const stationMapUrl = buildStationMapUrl(routeStation);

    return (
      <section className="station-detail-page">
        <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        <div className="station-detail-card">
          <h1>{routeStation.brand}</h1>
          <p className="station-detail-address">{routeStation.address}, {routeStation.city}</p>
          <a
            className="station-map-link-button"
            href={stationMapUrl}
            onClick={() => logAnalyticsEvent('show_station_on_map_click', {
              station_id: routeStation.id,
              brand: routeStation.brand,
              city: routeStation.city
            })}
          >Rodyti šią degalinę žemėlapyje</a>

          <div className="station-detail-prices">
            <div className="station-detail-price-item">
              <span>Benzinas 95</span>
              <strong>{routeStation.gasPrice > 0 ? `${routeStation.gasPrice.toFixed(3)} €/L` : 'Nėra duomenų'}</strong>
            </div>
            <div className="station-detail-price-item">
              <span>Dyzelinas</span>
              <strong>{routeStation.dieselPrice > 0 ? `${routeStation.dieselPrice.toFixed(3)} €/L` : 'Nėra duomenų'}</strong>
            </div>
            <div className="station-detail-price-item">
              <span>Dujos</span>
              <strong>{routeStation.naturalGasPrice > 0 ? `${routeStation.naturalGasPrice.toFixed(3)} €/L` : 'Nėra duomenų'}</strong>
            </div>
          </div>

          <p className="station-detail-note">
            Kainos atnaujinamos iš oficialių tiekėjų pateikiamų duomenų.
          </p>
          <p className="station-detail-note">
            Plačiau: <a href={DATA_METHODOLOGY_PATH}>Duomenys ir metodika</a>
          </p>
          <p className="station-detail-note">
            Naršyti: <a href={`${CITY_PATH_PREFIX}/${normalizeForId(routeStation.city)}`}>{routeStation.city} kainos</a> •{' '}
            <a href={`${BRAND_PATH_PREFIX}/${normalizeForId(routeStation.brand)}`}>{routeStation.brand} tinklas</a>
          </p>
        </div>
      </section>
    );
  };

  const renderCityPage = () => {
    if (!routeCityPage) {
      return (
        <div className="empty-state">
          <MapPin size={64} className="empty-icon" />
          <h2>Miestas nerastas</h2>
          <p>Patikrinkite nuorodą arba grįžkite į bendrą žemėlapį.</p>
          <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        </div>
      );
    }

    const topGasStations = getTopStationsByFuel(routeCityPage.stations, 'gas', 10);
    const cheapestGas = topGasStations[0] ? getFuelPrice(topGasStations[0], 'gas') : 0;
    const cheapestDiesel = getTopStationsByFuel(routeCityPage.stations, 'diesel', 1)[0];

    return (
      <section className="info-page">
        <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        <article className="info-card">
          <h1>{routeCityPage.name} degalinių kainos</h1>
          <p>
            Šiame mieste rasta <strong>{routeCityPage.stations.length}</strong> degalinių.
            Pigiausias benzinas: <strong>{formatPrice(cheapestGas)}</strong>.
            Pigiausias dyzelinas: <strong>{formatPrice(cheapestDiesel ? cheapestDiesel.dieselPrice : 0)}</strong>.
          </p>

          <section className="info-section">
            <h2>Top 10 pigiausių benzino kainų</h2>
            {topGasStations.length === 0 ? (
              <p>Šiuo metu nėra pakankamai duomenų benzino kainų reitingui.</p>
            ) : (
              <div className="entity-list">
                {topGasStations.map((station) => (
                  <a key={station.id} className="entity-list-item" href={`/degaline/${station.id}`}>
                    <span className="entity-list-main">{station.brand} • {station.address}</span>
                    <span className="entity-list-meta">{formatPrice(station.gasPrice)}</span>
                  </a>
                ))}
              </div>
            )}
          </section>

          <p className="info-links">
            Taip pat žr. <a href={DATA_METHODOLOGY_PATH}>Duomenys ir metodika</a>.
          </p>
        </article>
      </section>
    );
  };

  const renderBrandPage = () => {
    if (!routeBrandPage) {
      return (
        <div className="empty-state">
          <MapPin size={64} className="empty-icon" />
          <h2>Degalinių tinklas nerastas</h2>
          <p>Patikrinkite nuorodą arba grįžkite į bendrą žemėlapį.</p>
          <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        </div>
      );
    }

    const topGasStations = getTopStationsByFuel(routeBrandPage.stations, 'gas', 10);
    const cheapestGas = topGasStations[0] ? getFuelPrice(topGasStations[0], 'gas') : 0;
    const uniqueCities = new Set(routeBrandPage.stations.map((station) => station.city)).size;

    return (
      <section className="info-page">
        <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        <article className="info-card">
          <h1>{routeBrandPage.name} degalinių kainos</h1>
          <p>
            Tinkle rasta <strong>{routeBrandPage.stations.length}</strong> degalinių
            per <strong>{uniqueCities}</strong> miestų/savivaldybių.
            Pigiausias benzinas tinkle: <strong>{formatPrice(cheapestGas)}</strong>.
          </p>

          <section className="info-section">
            <h2>Top 10 pigiausių benzino kainų tinkle</h2>
            {topGasStations.length === 0 ? (
              <p>Šiuo metu nėra pakankamai duomenų benzino kainų reitingui.</p>
            ) : (
              <div className="entity-list">
                {topGasStations.map((station) => (
                  <a key={station.id} className="entity-list-item" href={`/degaline/${station.id}`}>
                    <span className="entity-list-main">{station.address} • {station.city}</span>
                    <span className="entity-list-meta">{formatPrice(station.gasPrice)}</span>
                  </a>
                ))}
              </div>
            )}
          </section>

          <p className="info-links">
            Taip pat žr. <a href={DATA_METHODOLOGY_PATH}>Duomenys ir metodika</a>.
          </p>
        </article>
      </section>
    );
  };

  const renderDataMethodologyPage = () => {
    return (
      <section className="info-page">
        <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        <article className="info-card">
          <h1>Duomenys ir metodika</h1>
          <p>
            Kuras123.lt pateikia Lietuvos degalinių kainas pagal viešai prieinamus šaltinius.
            Šis puslapis paaiškina, iš kur gaunami duomenys ir kaip jie apdorojami.
          </p>

          <section className="info-section">
            <h2>Duomenų šaltinis</h2>
            <p>
              Pirminis šaltinis: <a
                href="https://www.ena.lt/degalu-kainos-degalinese/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logAnalyticsEvent('outbound_source_click', {
                  source_name: 'ena',
                  url: 'https://www.ena.lt/degalu-kainos-degalinese/'
                })}
              >ENA skelbiami degalų kainų duomenys</a>.
              CSV failas periodiškai atnaujinamas ir importuojamas į sistemą.
            </p>
          </section>

          <section className="info-section">
            <h2>Atnaujinimo dažnis</h2>
            <p>
              Kainos atnaujinamos automatiškai. Paskutinio sėkmingo duomenų importo data:{' '}
              <strong>
                <time dateTime={dataUpdatedDate || undefined}>{formatSourceDateLt(dataUpdatedDate)}</time>
              </strong>
            </p>
          </section>

          <section className="info-section">
            <h2>Koordinačių tikslumas</h2>
            <p>
              Žemėlapyje naudojamos iš anksto sugeneruotos koordinatės. Kai tiksli adreso koordinatė nerandama,
              taikomas miesto centro rezervinis variantas. Rankiniai pataisymai saugomi atskirai.
            </p>
          </section>

          <section className="info-section">
            <h2>Apribojimai</h2>
            <ul>
              <li>Kainų duomenys priklauso nuo šaltinio pateikiamo atnaujinimo dažnio.</li>
              <li>Kai kurių degalinių duomenys gali būti laikini arba nepilni.</li>
              <li>Koordinatės gali turėti paklaidą, ypač naujų ar neįprastai pateiktų adresų atvejais.</li>
            </ul>
          </section>

          {renderEntityNavigationLinks({
            title: 'Teminės kainų suvestinės',
            cityLimit: 10,
            brandLimit: 10
          })}

          <p className="info-links">
            Taip pat žr. <a href={FAQ_PATH}>DUK</a>.
          </p>
        </article>
      </section>
    );
  };

  const renderFaqPage = () => {
    return (
      <section className="info-page">
        <a className="station-back-link" href="/">← Grįžti į žemėlapį</a>
        <article className="info-card">
          <h1>Dažniausiai užduodami klausimai (DUK)</h1>

          {FAQ_ITEMS.map((item) => (
            <section className="info-section" key={item.question}>
              <h2>{item.question}</h2>
              <p>{item.answer}</p>
            </section>
          ))}

          {renderEntityNavigationLinks({
            title: 'Naršykite kainas pagal miestą ir tinklą',
            cityLimit: 10,
            brandLimit: 10
          })}

          <p className="info-links">
            Daugiau informacijos: <a href={DATA_METHODOLOGY_PATH}>Duomenys ir metodika</a>.
          </p>
        </article>
      </section>
    );
  };

  const renderPriceAlertsPanel = () => {
    return (
      <div
        className="filter-dropdown-menu alert-dropdown-menu popup-panel expanded"
        aria-hidden={false}
      >
        <div className="price-alert-section">
          <h2>Kainos įspėjimai</h2>
          <p className="price-alert-description">
            Gaukite pranešimą, kai bet kurios pasirinkto tinklo degalinės kaina pasieks jūsų nurodytą ribą.
          </p>

          <form className="price-alert-form" onSubmit={handleCreatePriceAlert}>
            <div className="price-alert-form-row">
              <label htmlFor="alertBrand">Degalinių tinklas</label>
              <select
                id="alertBrand"
                value={alertBrandKey}
                onChange={(event) => {
                  const nextBrandKey = event.target.value;
                  setAlertBrandKey(nextBrandKey);
                  const selectedOption = alertBrandOptions.find((brand) => brand.key === nextBrandKey);
                  logAnalyticsEvent('select_alert_brand', {
                    brand: selectedOption?.name,
                    brand_key: nextBrandKey
                  });
                }}
              >
                {alertBrandOptions.length === 0 && (
                  <option value="">Nėra degalinių tinklų</option>
                )}
                {alertBrandOptions.map((brand) => (
                  <option key={brand.key} value={brand.key}>{brand.name} ({brand.count})</option>
                ))}
              </select>
            </div>

            <div className="price-alert-form-row">
              <label htmlFor="alertFuelType">Kuro tipas</label>
              <select
                id="alertFuelType"
                value={alertFuelType}
                onChange={(event) => {
                  setAlertFuelType(event.target.value);
                  logAnalyticsEvent('select_alert_fuel', {
                    fuel_type: event.target.value
                  });
                }}
              >
                {fuelOptions.map((fuel) => (
                  <option key={fuel.value} value={fuel.value}>{FUEL_LABELS[fuel.value]}</option>
                ))}
              </select>
            </div>

            <div className="price-alert-form-row">
              <label htmlFor="alertTargetPrice">Pranešti kai kaina ≤</label>
              <input
                id="alertTargetPrice"
                type="number"
                min="0"
                step="0.001"
                value={alertTargetPrice}
                onChange={(event) => setAlertTargetPrice(event.target.value)}
                placeholder="1.399"
              />
            </div>

            <button type="submit" className="price-alert-submit" disabled={alertBrandOptions.length === 0}>Išsaugoti įspėjimą</button>
          </form>

          {alertFeedback && <p className="price-alert-feedback">{alertFeedback}</p>}

          {priceAlerts.length > 0 && (
            <div className="price-alert-list">
              {priceAlerts.map((alert) => {
                const livePrice = getBestBrandFuelPrice(gasStations, alert.brandKey, alert.fuelType);
                const hasLivePrice = Number.isFinite(livePrice) && livePrice > 0;

                return (
                  <div key={alert.id} className="price-alert-item">
                    <div className="price-alert-item-main">
                      <strong>{alert.brandLabel} • {FUEL_LABELS[alert.fuelType]} ≤ {alert.targetPrice.toFixed(3)} €/L</strong>
                      <span>
                        {alert.active
                          ? `Aktyvus • mažiausia tinklo kaina ${hasLivePrice ? `${livePrice.toFixed(3)} €/L` : 'nėra duomenų'}`
                          : `Suveikė ${formatAlertDateTime(alert.triggeredAt)}${alert.triggeredPrice ? ` • ${alert.triggeredPrice.toFixed(3)} €/L` : ''}${alert.triggeredStationAddress ? ` • ${alert.triggeredStationAddress}` : ''}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="price-alert-delete"
                      onClick={() => handleDeletePriceAlert(alert.id)}
                    >
                      Pašalinti
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMobileFiltersPanel = () => {
    return (
      <div
        className="mobile-floating-panel mobile-filters-panel popup-panel expanded"
        ref={mobileFiltersRef}
        aria-hidden={false}
      >
        <div className="mobile-filter-panel-header">
          <h2>Filtrai</h2>
        </div>

        <div className="mobile-filter-section">
          <h3>Degalinių tinklas</h3>
          <div className="mobile-filter-options">
            <button
              type="button"
              onClick={() => handleSelectBrandFilter('all', 'mobile')}
              className={`mobile-filter-option ${selectedBrand === 'all' ? 'active' : ''}`}
            >
              Visi tinklai
            </button>
            {brands.map((brand) => (
              <button
                key={brand.name}
                type="button"
                onClick={() => handleSelectBrandFilter(brand.name, 'mobile')}
                className={`mobile-filter-option ${selectedBrand === brand.name ? 'active' : ''}`}
              >
                {brand.name} ({brand.count})
              </button>
            ))}
          </div>
        </div>

        <div className="mobile-filter-section">
          <h3>Kuro tipas</h3>
          <div className="mobile-filter-options">
            {fuelOptions.map((fuel) => (
              <button
                key={fuel.value}
                type="button"
                onClick={() => handleSelectFuelFilter(fuel.value, 'mobile')}
                className={`mobile-filter-option ${selectedFuel === fuel.value ? 'active' : ''}`}
              >
                {fuel.label}
              </button>
            ))}
          </div>
        </div>

      </div>
    );
  };


  return (
    <div className={`app panel-theme-${panelTheme}`}>
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
      <main className="main-content">
        {isDataMethodologyRoute ? (
          renderDataMethodologyPage()
        ) : isFaqRoute ? (
          renderFaqPage()
        ) : loading ? (
          <div className="empty-state">
            <div className="loader"></div>
            <h2>Kraunami duomenys...</h2>
          </div>
        ) : error ? (
          <div className="empty-state">
            <MapPin size={64} className="empty-icon" />
            <div className="error-message">
              <p>{error}</p>
            </div>
          </div>
        ) : isStationRoute ? (
          renderStationDetail()
        ) : isCityRoute ? (
          renderCityPage()
        ) : isBrandRoute ? (
          renderBrandPage()
        ) : (
          <>
            {/* Floating Brand Filter - Top Left */}
            <div className={`floating-filter floating-filter-left desktop-floating-filter ${showBrandDropdown ? 'open' : ''}`} ref={brandFilterRef}>
              <button
                type="button"
                className={`filter-circle-button ${showBrandDropdown ? 'active' : ''}`}
                onClick={handleToggleBrandDropdown}
                aria-label="Pasirinkti prekės ženklą"
              >
                <Filter size={18} />
              </button>

              <div
                className={`filter-dropdown-menu popup-panel ${showBrandDropdown ? 'expanded' : 'collapsed'}`}
                aria-hidden={!showBrandDropdown}
              >
                  <button
                    type="button"
                    onClick={() => handleSelectBrandFilter('all', 'desktop')}
                    className={`filter-dropdown-item ${selectedBrand === 'all' ? 'active' : ''}`}
                  >
                    <span className="brand-option">
                      <span className="brand-option-logos-stack" aria-hidden="true">
                        {brands.slice(0, 3).map((topBrand, index) => {
                          const topLogoPath = getBrandLogoPath(topBrand.name);
                          return (
                            <span key={`${topBrand.name}-${index}`} className="brand-option-logo stacked">
                              {topLogoPath ? (
                                <img src={topLogoPath} alt={topBrand.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                              ) : (
                                '⛽'
                              )}
                            </span>
                          );
                        })}
                      </span>
                      <span className="brand-option-name">Visi</span>
                    </span>
                  </button>
                  {brands.map((brand) => {
                    const logoPath = getBrandLogoPath(brand.name);
                    return (
                    <button
                      key={brand.name}
                      type="button"
                      onClick={() => handleSelectBrandFilter(brand.name, 'desktop')}
                      className={`filter-dropdown-item ${selectedBrand === brand.name ? 'active' : ''}`}
                    >
                      <span className="brand-option">
                        <span className="brand-option-logo">
                          {logoPath ? (
                            <img src={logoPath} alt={brand.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          ) : (
                            '⛽'
                          )}
                        </span>
                        <span className="brand-option-name">{brand.name}</span>
                        <span className="brand-option-count">{brand.count}</span>
                      </span>
                    </button>
                    );
                  })}
                </div>
            </div>

            {/* Floating Fuel Type Filter - Top Right */}
            <div className={`floating-filter floating-filter-right desktop-floating-filter ${showFuelDropdown ? 'open' : ''}`} ref={fuelFilterRef}>
              <button
                type="button"
                className={`filter-circle-button ${showFuelDropdown ? 'active' : ''}`}
                onClick={handleToggleFuelDropdown}
                aria-label="Pasirinkti kuro tipą"
              >
                <Fuel size={18} />
              </button>

              <div
                className={`filter-dropdown-menu align-right popup-panel ${showFuelDropdown ? 'expanded' : 'collapsed'}`}
                aria-hidden={!showFuelDropdown}
              >
                  {fuelOptions.map((fuel) => (
                    <button
                      key={fuel.value}
                      type="button"
                      onClick={() => handleSelectFuelFilter(fuel.value, 'desktop')}
                      className={`filter-dropdown-item ${selectedFuel === fuel.value ? 'active' : ''}`}
                    >
                      {fuel.label}
                    </button>
                  ))}
                </div>
            </div>

            <div className={`floating-filter floating-filter-alert ${showAlertPanel ? 'open' : ''}`} ref={alertFilterRef}>
              <button
                type="button"
                className={`filter-circle-button desktop-alert-toggle ${showAlertPanel ? 'active' : ''}`}
                onClick={handleToggleAlertPanel}
                aria-label="Kainos įspėjimai"
              >
                <Bell size={18} />
              </button>

              {showAlertPanel && renderPriceAlertsPanel()}
            </div>

            {showMobileFiltersPanel && renderMobileFiltersPanel()}

            <GasStationMap 
              stations={filteredStations} 
              onBoundsChange={setMapBounds}
              selectedFuel={selectedFuel}
              locateRequestId={locateRequestId}
              stationFocusTarget={mapFocusTarget}
              onStationPopupOpen={handleStationPopupOpen}
              onStationDetailLinkClick={handleStationDetailLinkClick}
              onLocationResult={handleLocationResult}
            />
            <BestPricesPanel
              visibleStations={visibleStations}
              isExpanded={isBestPricesExpanded}
              onExpandedChange={handleBestPricesExpandedChange}
              onStationLinkClick={(station, fuelType, rank) => logAnalyticsEvent('best_price_station_click', {
                station_id: station.id,
                brand: station.brand,
                city: station.city,
                fuel_type: fuelType,
                rank
              })}
            />

            <div className="mobile-action-bar" role="toolbar" aria-label="Pagrindiniai veiksmai" ref={mobileActionBarRef}>
              <button
                type="button"
                className={`mobile-action-button ${showMobileFiltersPanel ? 'active' : ''}`}
                onClick={handleToggleMobileFiltersPanel}
              >
                <Filter size={16} />
                <span>Filtrai</span>
              </button>
              <button
                type="button"
                className={`mobile-action-button ${isBestPricesExpanded ? 'active' : ''}`}
                onClick={handleToggleBestPricesPanel}
              >
                <TrendingDown size={16} />
                <span>Pigiausios</span>
              </button>
              <button
                type="button"
                className={`mobile-action-button ${showAlertPanel ? 'active' : ''}`}
                onClick={handleToggleAlertPanel}
              >
                <Bell size={16} />
                <span>Įspėjimai</span>
              </button>
              <button
                type="button"
                className="mobile-action-button"
                onClick={handleLocateUser}
              >
                <MapPin size={16} />
                <span>Mano vieta</span>
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
